import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Save,
  Check,
  Image as ImageIcon,
  FileText,
  Users,
  Award,
  Target,
  Heart,
  Upload,
  X,
  Loader2,
  AlertCircle,
  TrendingUp,
  Star,
  Zap,
  Globe,
  CheckCircle,
  Sparkles,
  Edit,
  Trash2
} from 'lucide-react';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import Base from '../Base';

// Section icons mapping
const SECTION_ICONS = {
  hero: ImageIcon,
  story: FileText,
  mission: Target,
  vision: Award,
  values: Heart,
  team: Users,
  timeline: TrendingUp,
  whyChooseUs: Star,
  culture: Globe,
  stats: Award,
  cta: Zap,
  achievements: Award
};

const AboutPageForm = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [error, setError] = useState(null);
  const [baseTemplate, setBaseTemplate] = useState(null);

  // File upload states
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [previewImages, setPreviewImages] = useState({});

  // Modal states
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [modalErrorMessage, setModalErrorMessage] = useState('');

  const [formData, setFormData] = useState({});
  const [availableSections, setAvailableSections] = useState([]);

  useEffect(() => {
    loadPageData();
  }, [templateId]);

  // Helper: apply base template structure (blocks, sections, defaults)
  const applyBaseTemplate = (template, savedContent = null) => {
    setBaseTemplate(template);

    if (template.blocks && Array.isArray(template.blocks)) {
      const enabledSectionIds = template.blocks
        .filter(block => block.enabled === true)
        .map(block => block.id);

      setAvailableSections(enabledSectionIds);

      if (enabledSectionIds.length > 0) {
        setActiveSection(enabledSectionIds[0]);
      }

      // Start with default content from template, then merge saved content on top
      if (template.content) {
        const filteredContent = {};
        enabledSectionIds.forEach(sectionId => {
          if (template.content[sectionId]) {
            filteredContent[sectionId] = template.content[sectionId];
          }
        });

        if (savedContent && Object.keys(savedContent).length > 0) {
          // Deep merge: for each section, merge saved data over defaults
          enabledSectionIds.forEach(sectionId => {
            if (savedContent[sectionId]) {
              filteredContent[sectionId] = {
                ...(filteredContent[sectionId] || {}),
                ...savedContent[sectionId]
              };
            }
          });
        }

        setFormData(filteredContent);
      }
    }
  };

  const loadPageData = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('access_token');

      // First, always load the base template for structure (blocks, theme, defaults)
      let baseTemplateData = null;
      try {
        const baseResponse = await axios.get(`/static/template_bases/about/about${templateId}.json`);
        baseTemplateData = baseResponse.data;
      } catch (baseErr) {
        // Base template is required for structure
        setError('Failed to load template structure. Please try again.');
        return;
      }

      // Then try to load saved data from API
      try {
        const apiResponse = await axios.get(
          `${API_BASE_URL}/api/template/${templateId}/page/about`,
          { headers: { 'Authorization': token ? `Bearer ${token}` : '' } }
        );

        if (apiResponse.data && apiResponse.data.content && !apiResponse.data.is_new) {
          // Saved data exists - merge with base template structure
          applyBaseTemplate(baseTemplateData, apiResponse.data.content);
        } else {
          // No saved data (new page) - use base template defaults only
          applyBaseTemplate(baseTemplateData);
        }
      } catch (apiErr) {
        if (apiErr.response?.status === 401) { navigate('/login'); return; }
        // API failed - fall back to base template defaults
        applyBaseTemplate(baseTemplateData);
      }

    } catch (err) {
      if (err.response?.status === 401) { navigate('/login'); return; }
      setError('Failed to load template. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle input changes
  const handleInputChange = (path, value) => {
    setFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  // Handle file uploads
  const handleFileUpload = (fieldName, file) => {
    if (file) {
      setUploadedFiles(prev => ({ ...prev, [fieldName]: file }));

      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreviewImages(prev => ({ ...prev, [fieldName]: reader.result }));
        };
        reader.readAsDataURL(file);
      }
    }
  };

  // Remove uploaded file
  const removeFile = (fieldName) => {
    setUploadedFiles(prev => {
      const newFiles = { ...prev };
      delete newFiles[fieldName];
      return newFiles;
    });
    setPreviewImages(prev => {
      const newPreviews = { ...prev };
      delete newPreviews[fieldName];
      return newPreviews;
    });
  };

  // Array handlers
  const addArrayItem = (path, defaultItem) => {
    const keys = path.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current = current[keys[i]];
      }

      const arrayKey = keys[keys.length - 1];
      if (!Array.isArray(current[arrayKey])) {
        current[arrayKey] = [];
      }
      current[arrayKey] = [...current[arrayKey], defaultItem];

      return newData;
    });
  };

  const updateArrayItem = (path, index, field, value) => {
    const keys = path.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }

      const arrayKey = keys[keys.length - 1];
      if (Array.isArray(current[arrayKey])) {
        current[arrayKey][index] = { ...current[arrayKey][index], [field]: value };
      }

      return newData;
    });
  };

  const removeArrayItem = (path, index) => {
    const keys = path.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }

      const arrayKey = keys[keys.length - 1];
      if (Array.isArray(current[arrayKey]) && current[arrayKey].length > 1) {
        current[arrayKey] = current[arrayKey].filter((_, i) => i !== index);
      }

      return newData;
    });
  };

  // File Upload Area Component with improved UI
  const FileUploadArea = ({ fieldName, label, accept = "image/*", dimensions = "", description = "", existingUrl = "" }) => {
    const previewSrc = previewImages[fieldName] || existingUrl;

    return (
      <div className="space-y-3">
        {/* Label with badge */}
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm font-semibold text-gray-800">
            <ImageIcon className="w-4 h-4 text-gray-500" />
            {label}
          </label>
          {dimensions && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
              {dimensions}
            </span>
          )}
        </div>
        {description && <p className="text-xs text-gray-500 italic">{description}</p>}

        {previewSrc ? (
          // Beautiful image preview card
          <div className="relative group">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-2xl opacity-30 group-hover:opacity-50 blur transition-all duration-300"></div>
            <div className="relative aspect-video w-full max-w-sm overflow-hidden rounded-xl bg-white shadow-lg">
              <img
                src={previewSrc}
                alt="Preview"
                className="absolute inset-0 w-full h-full object-cover"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              {/* Gradient overlay on hover */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300">
                <div className="absolute bottom-0 left-0 right-0 p-4 flex items-center justify-center gap-3">
                  <label className="flex items-center gap-2 px-4 py-2 bg-white/90 backdrop-blur-sm text-gray-800 rounded-lg cursor-pointer hover:bg-white transition-colors font-medium text-sm shadow-lg">
                    <Edit className="w-4 h-4" />
                    Change
                    <input
                      type="file"
                      accept={accept}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleFileUpload(fieldName, file);
                      }}
                      className="hidden"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => removeFile(fieldName)}
                    className="flex items-center gap-2 px-4 py-2 bg-red-500/90 backdrop-blur-sm text-white rounded-lg hover:bg-red-600 transition-colors font-medium text-sm shadow-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                    Remove
                  </button>
                </div>
              </div>
              {/* Success indicator */}
              <div className="absolute top-3 right-3 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                <Check className="w-5 h-5 text-white" />
              </div>
            </div>
          </div>
        ) : (
          // Beautiful upload area
          <label className="block cursor-pointer group">
            <div className="relative">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-2xl opacity-50 group-hover:opacity-0 transition-all duration-300"></div>
              <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 rounded-2xl opacity-0 group-hover:opacity-50 blur transition-all duration-300"></div>
              <div className="relative aspect-video max-w-sm border-2 border-dashed border-gray-300 group-hover:border-transparent rounded-xl bg-gradient-to-br from-gray-50 to-white group-hover:from-blue-50 group-hover:to-purple-50 transition-all duration-300 overflow-hidden">
                <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-100 to-purple-100 group-hover:from-blue-200 group-hover:to-purple-200 flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110">
                    <Upload className="w-8 h-8 text-blue-500 group-hover:text-purple-600 transition-colors" />
                  </div>
                  <p className="text-sm font-medium text-gray-700 group-hover:text-purple-700 transition-colors text-center">
                    Click to upload
                  </p>
                  <p className="text-xs text-gray-400 group-hover:text-purple-400 mt-1 transition-colors">
                    PNG, JPG, GIF, SVG
                  </p>
                  <div className="mt-3 flex items-center gap-1 text-xs text-gray-400">
                    <Sparkles className="w-3 h-3" />
                    <span>Drag & drop supported</span>
                  </div>
                </div>
              </div>
            </div>
            <input
              type="file"
              accept={accept}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileUpload(fieldName, file);
              }}
              className="hidden"
            />
          </label>
        )}
      </div>
    );
  };

  // Success Modal Component - Vasstr colors
  const SuccessModal = () => {
    if (!showSuccessModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={handleSuccessModalClose}></div>
        <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden border border-gray-200">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-[#D45614]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#D45614]" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Saved Successfully!</h3>
            <p className="text-gray-600 text-sm mb-6">Your about page has been saved.</p>
            <button onClick={handleSuccessModalClose} className="w-full bg-[#D45614] hover:bg-[#b8480f] text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
              <Check className="w-4 h-4" />
              Continue to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Error Modal Component - Simple design
  const ErrorModal = () => {
    if (!showErrorModal) return null;
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/50" onClick={() => setShowErrorModal(false)}></div>
        <div className="relative bg-white rounded-2xl shadow-xl max-w-sm w-full overflow-hidden border border-gray-200">
          <div className="p-6 text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Something went wrong</h3>
            <p className="text-gray-600 text-sm mb-6">{modalErrorMessage}</p>
            <button onClick={() => setShowErrorModal(false)} className="w-full bg-gray-800 hover:bg-gray-900 text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
              <X className="w-4 h-4" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render field based on key name and value type
  const renderField = (key, value, path) => {
    const fieldPath = path ? `${path}.${key}` : key;
    const isImageField = key.toLowerCase().includes('image') || key.toLowerCase().includes('background');

    if (isImageField && typeof value === 'string') {
      return (
        <FileUploadArea
          key={fieldPath}
          fieldName={fieldPath}
          label={formatLabel(key)}
          accept="image/*"
          dimensions="1920x1080px"
          existingUrl={value || ''}
        />
      );
    }

    if (typeof value === 'string') {
      const isLongText = key.toLowerCase().includes('content') ||
                        key.toLowerCase().includes('description') ||
                        key.toLowerCase().includes('bio');

      if (isLongText) {
        return (
          <div key={fieldPath}>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {formatLabel(key)}
            </label>
            <textarea
              value={value || ''}
              onChange={(e) => handleInputChange(fieldPath, e.target.value)}
              placeholder={`Enter ${formatLabel(key).toLowerCase()}`}
              rows={5}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        );
      }

      return (
        <div key={fieldPath}>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {formatLabel(key)}
          </label>
          <input
            type="text"
            value={value || ''}
            onChange={(e) => handleInputChange(fieldPath, e.target.value)}
            placeholder={`Enter ${formatLabel(key).toLowerCase()}`}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      );
    }

    return null;
  };

  // Format label from camelCase
  const formatLabel = (str) => {
    return str
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (s) => s.toUpperCase())
      .trim();
  };

  // Render array section
  const renderArraySection = (sectionKey, arrayKey, items, path) => {
    const arrayPath = path ? `${path}.${arrayKey}` : `${sectionKey}.${arrayKey}`;

    if (!Array.isArray(items) || items.length === 0) return null;

    const itemTemplate = items[0];

    return (
      <div className="space-y-6">
        <h4 className="text-lg font-semibold text-gray-900 capitalize">
          {formatLabel(arrayKey)}
        </h4>

        {items.map((item, index) => (
          <div key={index} className="p-6 bg-white rounded-xl border border-gray-100 space-y-4">
            <div className="flex items-center justify-between">
              <h5 className="text-md font-semibold text-gray-900 capitalize">
                {formatLabel(arrayKey.replace(/s$/, ''))} {index + 1}
              </h5>
              {items.length > 1 && (
                <button
                  onClick={() => removeArrayItem(arrayPath, index)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {Object.entries(item).map(([key, value]) => {
              const isImageField = key.toLowerCase().includes('image') || key.toLowerCase().includes('icon');

              if (isImageField) {
                return (
                  <FileUploadArea
                    key={key}
                    fieldName={`${arrayPath}_${index}_${key}`}
                    label={formatLabel(key)}
                    accept="image/*"
                    dimensions="400x400px"
                    existingUrl={value || ''}
                  />
                );
              }

              const isLongText = key.toLowerCase().includes('description') || key.toLowerCase().includes('bio');

              return (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {formatLabel(key)}
                  </label>
                  {isLongText ? (
                    <textarea
                      value={value || ''}
                      onChange={(e) => updateArrayItem(arrayPath, index, key, e.target.value)}
                      placeholder={`Enter ${formatLabel(key).toLowerCase()}`}
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  ) : (
                    <input
                      type="text"
                      value={value || ''}
                      onChange={(e) => updateArrayItem(arrayPath, index, key, e.target.value)}
                      placeholder={`Enter ${formatLabel(key).toLowerCase()}`}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  )}
                </div>
              );
            })}
          </div>
        ))}

        <button
          onClick={() => addArrayItem(arrayPath, { ...itemTemplate })}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 transition-colors"
        >
          <Upload className="w-5 h-5" />
          <span>Add {formatLabel(arrayKey.replace(/s$/, ''))}</span>
        </button>
      </div>
    );
  };

  // Render section content dynamically
  const renderSectionContent = (sectionKey) => {
    const sectionData = formData[sectionKey];
    if (!sectionData) return null;

    const SectionIcon = SECTION_ICONS[sectionKey] || FileText;

    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <SectionIcon className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3 capitalize">
            {formatLabel(sectionKey)}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Configure your {formatLabel(sectionKey).toLowerCase()} section
          </p>
        </div>

        <div className="space-y-6">
          {Object.entries(sectionData).map(([key, value]) => {
            // Handle arrays (like items, members, events, reasons, images)
            if (Array.isArray(value)) {
              return (
                <div key={key}>
                  {renderArraySection(sectionKey, key, value, sectionKey)}
                </div>
              );
            }

            // Handle nested objects (but not arrays)
            if (typeof value === 'object' && value !== null) {
              return (
                <div key={key} className="space-y-4">
                  <h4 className="text-lg font-semibold text-gray-900 capitalize">
                    {formatLabel(key)}
                  </h4>
                  {Object.entries(value).map(([subKey, subValue]) =>
                    renderField(subKey, subValue, `${sectionKey}.${key}`)
                  )}
                </div>
              );
            }

            // Handle simple fields (string, number, etc.)
            return renderField(key, value, sectionKey);
          })}
        </div>
      </div>
    );
  };

  // Save handler
  const handleSave = async (isDraft = true) => {
    try {
      setSaving(true);
      setError(null);

      // Get authentication token
      const token = localStorage.getItem('access_token');
      if (!token) {
        setModalErrorMessage('Please login to save changes');
        setShowErrorModal(true);
        navigate('/login');
        return;
      }

      // Build request - use FormData if there are file uploads, otherwise plain JSON
      let response;

      if (Object.keys(uploadedFiles).length > 0) {
        const formDataToSend = new FormData();
        formDataToSend.append('status', isDraft ? 'draft' : 'complete');
        formDataToSend.append('content', JSON.stringify(formData));

        // Add all uploaded files
        Object.entries(uploadedFiles).forEach(([fieldName, file]) => {
          formDataToSend.append(fieldName, file);
        });

        response = await axios.post(
          `${API_BASE_URL}/api/template/${templateId}/page/about/save`,
          formDataToSend,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/api/template/${templateId}/page/about/save`,
          {
            status: isDraft ? 'draft' : 'complete',
            content: formData
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
      }

      if (response.data.success) {
        setLastSaved(new Date().toLocaleTimeString());
        setShowSuccessModal(true);
      } else {
        setModalErrorMessage(response.data.error || 'Failed to save about page');
        setShowErrorModal(true);
      }
    } catch (err) {
      if (err.response?.status === 401) { navigate('/login'); return; }
      setModalErrorMessage(err.response?.data?.error || 'Failed to save about page');
      setShowErrorModal(true);
    } finally {
      setSaving(false);
    }
  };

  // Handle modal close and navigation
  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate(`/template-builder/${templateId}`);
  };

  if (loading) {
    return (
      <Base Content={
        <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading about page template...</p>
        </div>
      </div>
    } />
    );
  }

  if (error && !baseTemplate) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-sm border border-red-200 max-w-md">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2 text-center">Error Loading Template</h2>
          <p className="text-gray-600 text-center mb-4">{error}</p>
          <button
            onClick={() => navigate(`/template-builder/${templateId}`)}
            className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const FormContent = (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(`/template-builder/${templateId}`)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-lg font-bold text-gray-900">
                  About Page - Template {templateId}
                </h1>
                <p className="text-xs text-gray-500">
                  {lastSaved ? `Last saved: ${lastSaved}` : baseTemplate?.template_name}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => handleSave(true)}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>Save Draft</span>
                  </>
                )}
              </button>
              <button
                onClick={() => handleSave(false)}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check className="w-4 h-4" />
                <span>Save & Exit</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <div className="w-64 flex-shrink-0">
            <div className="sticky top-24 bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 px-2">
                Sections
              </h3>
              <nav className="space-y-1">
                {availableSections.map((sectionKey) => {
                  const Icon = SECTION_ICONS[sectionKey] || FileText;
                  const isActive = activeSection === sectionKey;

                  return (
                    <button
                      key={sectionKey}
                      onClick={() => setActiveSection(sectionKey)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-blue-50 text-blue-700 border border-blue-200'
                          : 'text-gray-700 hover:bg-white border border-transparent'
                      }`}
                    >
                      <Icon className={`w-5 h-5 ${isActive ? 'text-blue-600' : 'text-gray-400'}`} />
                      <span className="capitalize">{formatLabel(sectionKey)}</span>
                      {isActive && (
                        <Check className="w-4 h-4 ml-auto text-blue-600" />
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Form Content */}
          <div className="flex-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {activeSection && renderSectionContent(activeSection)}

              {/* Section Navigation Buttons */}
              {availableSections.length > 1 && (
                <div className="mt-8 pt-6 border-t border-gray-100 flex justify-between items-center">
                  <button
                    onClick={() => {
                      const currentIndex = availableSections.findIndex(s => s === activeSection);
                      if (currentIndex > 0) {
                        setActiveSection(availableSections[currentIndex - 1]);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                    disabled={availableSections.findIndex(s => s === activeSection) === 0}
                    className="flex items-center space-x-2 px-6 py-3 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    <span className="font-medium">Previous Section</span>
                  </button>

                  <div className="text-sm text-gray-600 font-medium">
                    Section {availableSections.findIndex(s => s === activeSection) + 1} of {availableSections.length}
                  </div>

                  <button
                    onClick={() => {
                      const currentIndex = availableSections.findIndex(s => s === activeSection);
                      if (currentIndex < availableSections.length - 1) {
                        setActiveSection(availableSections[currentIndex + 1]);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                    disabled={availableSections.findIndex(s => s === activeSection) === availableSections.length - 1}
                    className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 rounded-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="font-medium">
                      {availableSections.findIndex(s => s === activeSection) === availableSections.length - 1 ? 'Last Section' : 'Next Section'}
                    </span>
                    {availableSections.findIndex(s => s === activeSection) < availableSections.length - 1 && (
                      <ArrowRight className="w-5 h-5" />
                    )}
                    {availableSections.findIndex(s => s === activeSection) === availableSections.length - 1 && (
                      <Check className="w-5 h-5" />
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <SuccessModal />
      <ErrorModal />
    </div>
  );

  return <Base Content={FormContent} />;
};

export default AboutPageForm;
